/**
 * ConversationPanel component - Main conversation interface.
 *
 * Design: DENiZEN — organic, warm, nature-inspired
 * - Rounded corners, soft shadows
 * - Stone header with warm stone message area
 * - WCAG 2.1 AA compliant
 */

import { useRef, useEffect, useState, useCallback } from 'react'
import type { ConversationTurn } from '@/models'
import type { InputType, InputOptions, ImageOption } from '@/services/api'
import type { TranscribeFunction } from '@/hooks/useSpeechToText'
import { TurnDisplay, SystemPrompt } from './TurnDisplay'
import { TextInput } from './TextInput'
import { TimeframeSlider, type TimeframeValue } from './TimeframeSlider'
import { InlineImageSelection } from '@/components/image'

/**
 * Topic-based dialogue progress tracking
 */
export interface DialogueProgress {
  topicsCovered: number
  topicsRequired: number
  isComplete: boolean
  coveredTopics?: string[]  // Actual topic names
}

/**
 * Format topic name for display (snake_case -> Title Case)
 */
function formatTopicName(topic: string): string {
  return topic
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export interface ConversationPanelProps {
  turns: ConversationTurn[]
  onSendMessage: (message: string, imageData?: string) => void
  isLoading?: boolean
  isDialogueComplete?: boolean
  progress?: DialogueProgress
  error?: string | null
  welcomeMessage?: string
  inputType?: InputType
  inputOptions?: InputOptions | null
  isGeneratingImages?: boolean
  imageOptions?: ImageOption[] | null
  onImageSelect?: (image: ImageOption) => void
  awaitingImageConfirmation?: boolean
  onConfirmImageGeneration?: () => void
  onModifyConversation?: () => void
  modifyConversationError?: string | null
  imageGenerationUnavailable?: boolean
  /** @security Addresses issue #75 — transcribes via backend API for secure voice input */
  transcribeAudio?: TranscribeFunction
  /** Hidden by default — "Add more" flow not yet in scope; set to true to re-enable */
  showAddMoreButton?: boolean
  /** Shows ready/add-more buttons. @see Issue #173 */
  awaitingFinalConfirmation?: boolean
  onConfirmReady?: () => void
  onAddMore?: () => void
  /** Facilitator shortcut for early image generation. @see Issue #197 */
  onEarlyComplete?: () => void
  isEarlyCompleting?: boolean
  earlyCompleteError?: string | null
  /** Transient error from confirmation button handlers. @see Issue #250 */
  confirmationError?: string | null
}

export function ConversationPanel({
  turns,
  onSendMessage,
  isLoading = false,
  isDialogueComplete = false,
  progress,
  error,
  welcomeMessage = "Hello! I'm here to help you explore your vision for the future of your community. What do you think about where you live today?",
  inputType = 'text',
  inputOptions = null,
  isGeneratingImages = false,
  imageOptions = null,
  onImageSelect,
  awaitingImageConfirmation = false,
  onConfirmImageGeneration,
  onModifyConversation,
  modifyConversationError = null,
  imageGenerationUnavailable = false,
  transcribeAudio,
  showAddMoreButton = false,
  awaitingFinalConfirmation = false,
  onConfirmReady,
  onAddMore,
  onEarlyComplete,
  isEarlyCompleting = false,
  earlyCompleteError = null,
  confirmationError = null,
}: ConversationPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [turns, scrollToBottom])

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100
      setShowScrollButton(!isNearBottom)
    }
  }, [])

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header — stone bar (rounded-t-[40px] to match the GreenPageLayout card radius) */}
      <div className="flex-shrink-0 px-6 py-5 bg-stone border-b border-dark-green/15 rounded-t-[40px]">
        <div className="relative flex items-center">
          {/* Centre: Progress dots + status — absolutely centred relative to full header width */}
          <div className="absolute left-0 right-0 flex justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-1 pointer-events-auto">
              {progress && !isDialogueComplete && (
                <div
                  className="flex items-center gap-2"
                  role="group"
                  aria-label={`Conversation progress: ${progress.topicsCovered} of ${progress.topicsRequired} themes explored`}
                >
                  {Array.from({ length: progress.topicsRequired }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-4 h-4 rounded-full transition-colors ${
                        i < progress.topicsCovered
                          ? 'bg-light-green shadow-sm'
                          : 'border border-dark-green/30 bg-transparent'
                      }`}
                      title={progress.coveredTopics?.[i] ? formatTopicName(progress.coveredTopics[i]) : undefined}
                    />
                  ))}
                </div>
              )}
              {isDialogueComplete && (
                <div className="flex items-center gap-2 px-3 py-1 bg-light-green text-dark-green rounded-full font-display font-semibold text-xs shadow-denizen-sm">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Complete</span>
                </div>
              )}
              <p className="text-xs font-body text-dark-green/60" role="status" aria-live="polite">
                {isLoading ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-light-green animate-pulse" />
                    <span>Thinking...</span>
                  </span>
                ) : 'Ready to listen'}
              </p>
            </div>
          </div>

          {/* Right: Wordmark — text-only, right-aligned */}
          <div className="ml-auto flex items-center">
            <img
              src="/images/brand/Denizen_5.png"
              alt="DENiZEN"
              className="h-6 w-auto"
            />
          </div>
        </div>
      </div>

      {/* Messages area — warm stone background */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-4 relative bg-stone"
        role="log"
        aria-label="Conversation messages"
        aria-live="polite"
      >
        <SystemPrompt text={welcomeMessage} />

        <div className="space-y-4">
          {turns.map((turn) => (
            <div key={turn.turn_id} role="article">
              <TurnDisplay turn={turn} showTimestamp />
            </div>
          ))}
        </div>

        {/* Typing indicator */}
        {isLoading && (
          <div className="flex items-end" aria-live="polite">
            <div className="px-4 py-3 bg-white border border-dark-green rounded-[17px] shadow-denizen-sm">
              <div className="flex gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-mid-green animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2.5 h-2.5 rounded-full bg-mid-green animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2.5 h-2.5 rounded-full bg-mid-green animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
            <span className="sr-only">Assistant is typing...</span>
          </div>
        )}

        {/* Inline Image Selection */}
        {(isGeneratingImages || (imageOptions && imageOptions.length > 0)) && (
          <div className="flex items-start gap-3" aria-live="polite">
            <img
              src="/images/brand/DenizenLogoSmall.png"
              alt=""
              className="w-10 h-10 flex-shrink-0"
              aria-hidden="true"
            />
            <div className="px-4 py-4 bg-white rounded-2xl shadow-denizen-sm animate-fade-in">
              <InlineImageSelection
                images={imageOptions || []}
                onSelect={(image) => onImageSelect?.(image)}
                isLoading={false}
                isGenerating={isGeneratingImages}
              />
            </div>
          </div>
        )}

        {/* Final conversation confirmation (issue #173) */}
        {awaitingFinalConfirmation && !isLoading && !awaitingImageConfirmation && !isDialogueComplete && (
          <div className="flex flex-col items-center gap-3 py-4" role="group" aria-label="Conversation completion options">
            <p className="text-sm font-body text-dark-green/60 text-center max-w-md">
              Take a moment to think if there's anything else you'd like to share about your vision.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => onConfirmReady?.()}
                className="btn-denizen-primary px-6 py-3"
                aria-label="Ready to create my image"
              >
                Ready to create my image
              </button>
              <button
                type="button"
                onClick={() => onAddMore?.()}
                className="btn-denizen-secondary px-6 py-3"
                aria-label="I'd like to add something"
              >
                I'd like to add something
              </button>
            </div>

            {/* Always-present live region so screen readers detect content changes (issue #250) */}
            <div className="text-center" aria-live="assertive" aria-atomic="true">
              {confirmationError && (
                <div className="mt-3 px-4 py-2 bg-error/10 border border-error/20 rounded-xl">
                  <p className="text-sm font-body text-dark-green">{confirmationError}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Image confirmation buttons */}
        {awaitingImageConfirmation && !isLoading && !isGeneratingImages && (
          <div className="flex justify-center gap-4 py-4">
            <button
              type="button"
              onClick={() => onConfirmImageGeneration?.()}
              className="btn-denizen-primary px-6 py-3"
            >
              {inputOptions?.confirm_text || 'Yes, create my images!'}
            </button>
            {showAddMoreButton && (
              <button
                type="button"
                onClick={() => onModifyConversation?.()}
                className="btn-denizen-secondary px-6 py-3"
              >
                {inputOptions?.modify_text || "I'd like to add something first"}
              </button>
            )}
          </div>
        )}

        {/* Error feedback for modify conversation */}
        {modifyConversationError && (
          <div className="flex justify-center mt-1">
            <p className="text-sm text-error font-body break-words" role="alert">
              {modifyConversationError}
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />

        {/* Scroll to bottom button — absolute within relative scroll container */}
        {showScrollButton && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-white shadow-denizen-md flex items-center justify-center hover:-translate-y-1 transition-transform z-10"
            aria-label="Scroll to latest messages"
          >
            <svg className="w-6 h-6 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 py-3 bg-error/10 border-t border-error/20" role="alert">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-error flex items-center justify-center flex-shrink-0">
              <span className="text-white font-display font-bold text-sm">!</span>
            </div>
            <p className="text-sm font-body text-dark-green break-words">{error}</p>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 px-4 py-4 bg-stone border-t border-dark-green/15 rounded-b-[40px]">
        {imageGenerationUnavailable ? (
          <div className="text-center py-4">
            <div className="inline-flex flex-col items-center gap-2 px-6 py-4 bg-light-green/30 rounded-2xl">
              <svg className="w-8 h-8 text-dark-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-display font-semibold text-dark-green">
                Conversation complete
              </span>
              <span className="font-body text-sm text-dark-green/60 text-center max-w-md">
                Thank you for sharing your vision! Image generation is not available in this environment, but your insights have been recorded.
              </span>
            </div>
          </div>
        ) : isDialogueComplete ? (
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-light-green/30 rounded-2xl">
              <svg className="w-6 h-6 text-dark-green animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="font-display font-semibold text-dark-green">
                Creating your vision image...
              </span>
            </div>
          </div>
        ) : inputType === 'timeframe_slider' ? (
          <TimeframeSlider
            onSubmit={(timeframe: TimeframeValue) => onSendMessage(timeframe)}
            defaultIndex={inputOptions?.default_index ?? 4}
            isLoading={isLoading}
            disabled={isDialogueComplete}
            prompt={inputOptions?.prompt}
          />
        ) : (
          <TextInput
            onSubmit={onSendMessage}
            isLoading={isLoading}
            disabled={isDialogueComplete || awaitingImageConfirmation || awaitingFinalConfirmation || isEarlyCompleting}
            placeholder="Share your thoughts..."
            ariaLabel="Type your response to the conversation"
            isGeneratingImages={isGeneratingImages}
            transcribeAudio={transcribeAudio}
          />
        )}

        {/* Facilitator shortcut: early image generation (issue #197) */}
        {onEarlyComplete
          && !isDialogueComplete
          && !awaitingImageConfirmation
          && !awaitingFinalConfirmation
          && !isGeneratingImages
          && !imageGenerationUnavailable
          && turns.filter(t => t.speaker === 'user').length >= 2
          && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={onEarlyComplete}
              disabled={isLoading || isEarlyCompleting}
              className="px-3 py-1.5 text-dark-green bg-white border border-dark-green/20 rounded-full font-body text-sm transition-all hover:bg-light-green/30 hover:shadow-denizen-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Skip remaining questions and create image from current conversation"
              aria-label={`Create image now (${turns.filter(t => t.speaker === 'user').length} topics discussed)`}
            >
              {isEarlyCompleting ? (
                <span className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Preparing...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Create my image now
                </span>
              )}
            </button>
          </div>
        )}

        {/* Error feedback for early completion */}
        {earlyCompleteError && (
          <div className="flex justify-end mt-1">
            <p className="text-sm text-error font-body break-words" role="alert">
              {earlyCompleteError}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationPanel
